import { isIdn, toASCII, toUnicode } from './punycode';

/**
 * Client-side interpretation of the global search box. It only decides what a
 * query LOOKS like so the UI can offer the right jump links; the Search API does
 * the actual lookup. One query can have several plausible readings (for example
 * `ABC-12345` is both a valid ROID and a valid clTRID), so we return them all,
 * most specific first.
 *
 * Backend identifiers are UUIDv7 (`crates/common/src/ids.rs`) for every entity,
 * request, correlation and workflow ID, so a UUID alone cannot say which one it
 * is; for v7 we at least decode when it was minted.
 */

export type QueryKind =
  | 'domain'
  | 'uuid'
  | 'trace_id'
  | 'epp_trid'
  | 'roid'
  | 'email'
  | 'numeric_id'
  | 'free_text';

export type Confidence = 'high' | 'medium' | 'low';

export interface QueryAction {
  label: string;
  to: string;
}

export interface QueryCandidate {
  kind: QueryKind;
  label: string;
  detail: string;
  confidence: Confidence;
  actions: QueryAction[];
}

export interface QueryClassification {
  raw: string;
  normalized: string;
  candidates: QueryCandidate[];
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const TRACE_ID_RE = /^[0-9a-f]{32}$/i;
/** W3C traceparent: version-traceid-parentid-flags */
const TRACEPARENT_RE = /^[0-9a-f]{2}-([0-9a-f]{32})-[0-9a-f]{16}-[0-9a-f]{2}$/i;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const NUMERIC_RE = /^\d{3,}$/;
/** EPP clTRID / svTRID are 3–64 char tokens (RFC 5730 trIDStringType). We also require a digit and a separator. */
const TRID_RE = /^(?=.*\d)(?=.*[-_.:])[A-Za-z0-9][A-Za-z0-9._:-]{2,63}$/;
/** RFC 5730 roidType: (\w|_){1,80}-\w{1,8} */
const ROID_RE = /^[A-Za-z0-9_]{1,80}-[A-Za-z0-9]{1,8}$/;
const LDH_LABEL_RE = /^(?!-)[a-z0-9-]{1,63}(?<!-)$/;
const TLD_RE = /^(?:[a-z]{2,63}|xn--[a-z0-9-]{1,59})$/;

function enc(value: string): string {
  return encodeURIComponent(value);
}

function asDomain(query: string): { ascii: string; unicode: string } | null {
  if (!/[.。．｡]/.test(query) || /[\s@/\\:]/.test(query)) return null;
  let ascii: string;
  try {
    ascii = toASCII(query);
  } catch {
    return null;
  }
  const labels = ascii.split('.');
  const tld = labels[labels.length - 1] ?? '';
  if (labels.length < 2 || ascii.length > 253) return null;
  if (!labels.every((l) => LDH_LABEL_RE.test(l)) || !TLD_RE.test(tld)) return null;
  return { ascii, unicode: toUnicode(ascii) };
}

export function classifyQuery(raw: string): QueryClassification {
  const normalized = raw.trim();
  const candidates: QueryCandidate[] = [];
  if (!normalized) return { raw, normalized, candidates };

  const traceparent = TRACEPARENT_RE.exec(normalized);
  if (traceparent?.[1]) {
    const traceId = traceparent[1].toLowerCase();
    candidates.push({
      kind: 'trace_id',
      label: 'W3C traceparent',
      detail: `Trace ID ${traceId}`,
      confidence: 'high',
      actions: [{ label: 'Open trace', to: `/ops/traces/${traceId}` }],
    });
  }

  if (UUID_RE.test(normalized)) {
    const id = normalized.toLowerCase();
    const version = id.charAt(14);
    const minted = version === '7' ? new Date(parseInt(id.slice(0, 8) + id.slice(9, 13), 16)) : null;
    const mintedText = minted && !Number.isNaN(minted.getTime()) ? `minted ${minted.toISOString().slice(0, 16).replace('T', ' ')} UTC · ` : '';
    candidates.push({
      kind: 'uuid',
      label: `UUID (v${version})`,
      detail: `${mintedText}entity, request, correlation, workflow or registry-transaction ID`,
      confidence: 'high',
      actions: [
        { label: 'Search logs by request ID', to: `/ops/logs?request_id=${id}` },
        { label: 'Open as workflow', to: `/ops/workflows/${id}` },
        { label: 'Open as registry transaction', to: `/registry/transactions/${id}` },
      ],
    });
  }

  if (TRACE_ID_RE.test(normalized) && !/^0+$/.test(normalized)) {
    const id = normalized.toLowerCase();
    candidates.push({
      kind: 'trace_id',
      label: 'Trace ID',
      detail: '128-bit W3C trace ID',
      confidence: 'high',
      actions: [{ label: 'Open trace', to: `/ops/traces/${id}` }],
    });
  }

  if (EMAIL_RE.test(normalized)) {
    candidates.push({
      kind: 'email',
      label: 'Email address',
      detail: 'Customer or contact lookup',
      confidence: 'high',
      actions: [{ label: 'Customers', to: '/customers' }],
    });
  }

  const domain = candidates.length === 0 ? asDomain(normalized) : null;
  if (domain) {
    const idn = isIdn(normalized);
    const unicodeInput = /[\u0080-\u{10FFFF}]/u.test(normalized);
    candidates.push({
      kind: 'domain',
      label: idn ? (unicodeInput ? 'Domain name (Unicode IDN)' : 'Domain name (Punycode IDN)') : 'Domain name',
      detail: idn ? `${domain.unicode}  ·  ${domain.ascii}` : domain.ascii,
      confidence: 'high',
      actions: [{ label: 'Open domain', to: `/domains/${enc(domain.ascii)}` }],
    });
  }

  if (candidates.length === 0 && TRID_RE.test(normalized)) {
    candidates.push({
      kind: 'epp_trid',
      label: 'EPP transaction ID (clTRID / svTRID)',
      detail: 'Client or server transaction identifier from an EPP exchange',
      confidence: 'medium',
      actions: [{ label: 'Find registry transaction', to: `/registry/transactions?trid=${enc(normalized)}` }],
    });
  }

  if (!domain && ROID_RE.test(normalized)) {
    candidates.push({
      kind: 'roid',
      label: 'Registry object ID (ROID)',
      detail: 'Repository ID assigned by the registry',
      confidence: candidates.length === 0 ? 'medium' : 'low',
      actions: [{ label: 'Domains', to: `/domains?roid=${enc(normalized)}` }],
    });
  }

  if (candidates.length === 0 && NUMERIC_RE.test(normalized)) {
    candidates.push({
      kind: 'numeric_id',
      label: 'Numeric reference',
      detail: 'Order, invoice or customer number',
      confidence: 'low',
      actions: [
        { label: 'Orders', to: '/orders' },
        { label: 'Invoices', to: '/invoices' },
      ],
    });
  }

  if (candidates.length === 0) {
    candidates.push({
      kind: 'free_text',
      label: 'Free text',
      detail: 'Name search across domains, customers and subjects',
      confidence: 'low',
      actions: [],
    });
  }

  return { raw, normalized, candidates };
}

/** Short label for the inline hint beside the search box. */
export function shortKindLabel(kind: QueryKind): string {
  switch (kind) {
    case 'domain':
      return 'Domain';
    case 'uuid':
      return 'UUID';
    case 'trace_id':
      return 'Trace ID';
    case 'epp_trid':
      return 'clTRID / svTRID';
    case 'roid':
      return 'ROID';
    case 'email':
      return 'Email';
    case 'numeric_id':
      return 'Number';
    case 'free_text':
      return 'Text';
  }
}
